import React, { useMemo } from 'react';
import { loadMarketProfile, type MarketProfileState } from './marketProfileStore';
import { resolveMarketProfileCta, type MarketProfileNavTarget } from './marketProfileCta';
import { ProgressBar } from './MarketProfileShared';

type Props = {
  active: boolean;
  userEmail: string | null | undefined;
  profileTick: number;
  onOpen: (target: MarketProfileNavTarget) => void;
};

function useProfileCta(userEmail: string | null | undefined, profileTick: number) {
  return useMemo(() => {
    const state = loadMarketProfile(userEmail);
    const cta = resolveMarketProfileCta(state);
    return { state, cta };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, profileTick]);
}

export default function MarketProfileNavCard({ active, userEmail, profileTick, onOpen }: Props) {
  const { cta } = useProfileCta(userEmail, profileTick);

  const badgeClass = [
    'dcf-profile-tab__badge',
    cta.badgeOpen ? 'dcf-profile-tab__badge--open' : '',
    cta.badgeUpdate ? 'dcf-profile-tab__badge--update' : '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={`dcf-profile-tab ${active ? 'dcf-profile-tab--active' : ''}`}
      onClick={() => onOpen(cta.navTarget)}
      aria-label={`${cta.title}. ${cta.description}. ${cta.action}`}
    >
      <div className="dcf-profile-tab__head">
        <span className="dcf-profile-tab__icon">
          <i className="fas fa-chart-column" aria-hidden="true" />
        </span>
        <span className={badgeClass}>{cta.badge}</span>
      </div>

      <span className="dcf-profile-tab__title">{cta.title}</span>
      <span className="dcf-profile-tab__desc">{cta.description}</span>

      <div className="dcf-profile-tab__progress">
        <ProgressBar value={cta.progressPercent} />
      </div>

      <div className="dcf-profile-tab__footer">
        <span className="dcf-profile-tab__action">{cta.action}</span>
        {cta.showFillPrompt && (
          <span
            className="dcf-profile-tab__privacy-hint"
            title="Данные о компенсации не публикуются в сообществе."
            aria-label="Данные о компенсации не публикуются в сообществе."
          >
            <i className="fas fa-shield-halved" aria-hidden="true" />
          </span>
        )}
      </div>
    </button>
  );
}

export function useMarketProfileCtaState(
  userEmail: string | null | undefined,
  state?: MarketProfileState,
) {
  const profileState = state ?? loadMarketProfile(userEmail);
  return resolveMarketProfileCta(profileState);
}
